import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { diffWordsWithSpace } from "diff";
import { AlertCircle, Loader2, RefreshCw, Sparkles } from "lucide-react";
import {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  defineDoclistCategoryFromPages,
  getGetDoclistQueryKey,
  redefineDoclistCategoryFromPages,
} from "~/api/chat/chat";
import { Category } from "~/api/model";
import {
  getListOrganizationCategoriesQueryKey,
  getListOrganizationCategoryFoldersQueryKey,
  useUpdateOrganizationCategory,
} from "~/api/organization/organization";
import { AutoResizeTextarea } from "~/components/ui/auto-resize-textarea";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import { useToast } from "~/hooks/use-toast";
import logger from "~/utils/logger";

type Stage = "generating" | "review" | "error";
type Mode = "new" | "redefine";

interface DefineCategoryFormData {
  name: string;
  definition: string;
  namingConvention: string;
  namingExample: string;
  expectedContent: string;
  keyTopics: string;
  recognitionTips: string;
  length: string;
}

type FieldKey = keyof DefineCategoryFormData;

const emptyForm = (): DefineCategoryFormData => ({
  name: "",
  definition: "",
  namingConvention: "",
  namingExample: "",
  expectedContent: "",
  keyTopics: "",
  recognitionTips: "",
  length: "",
});

const toFormData = (category: Category): DefineCategoryFormData => ({
  name: category.name ?? "",
  definition: category.definition ?? "",
  namingConvention: category.namingConvention ?? "",
  namingExample: category.namingExample ?? "",
  expectedContent: category.expectedContent ?? "",
  keyTopics: category.keyTopics ?? "",
  recognitionTips: category.recognitionTips ?? "",
  length: category.length ?? "",
});

interface DefineCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  projectId: string;
  folderCategoryId: number;
  folderCategoryName: string;
  sectionCount: number;
  mode?: Mode;
}

/**
 * Blocking, multi-stage modal for the per-category "Add Category" / "Refine
 * Category" flow.
 *
 * Stages:
 *  - `generating`: shows a spinner while the backend runs the pages through
 *    the `define-category` (mode="new") or `redefine-category` (mode="redefine")
 *    prompt. Cancel aborts the fetch and closes.
 *  - `review`: the AI result is already persisted server-side; the form is
 *    pre-populated so the admin can tweak the definition before saving. In
 *    redefine mode each field also shows a dimmed diff against the previous
 *    value so the admin can see what changed.
 *  - `error`: surfaces the backend message with Retry / Close actions.
 */
const DefineCategoryModal: FC<DefineCategoryModalProps> = ({
  open,
  onOpenChange,
  organizationId,
  projectId,
  folderCategoryId,
  folderCategoryName,
  sectionCount,
  mode = "new",
}) => {
  const intl = useIntl();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isRedefine = mode === "redefine";

  const [stage, setStage] = useState<Stage>("generating");
  const [category, setCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<DefineCategoryFormData>(emptyForm);
  const [previousData, setPreviousData] =
    useState<DefineCategoryFormData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const { mutateAsync: updateCategory, isPending: isSaving } =
    useUpdateOrganizationCategory();

  const runGeneration = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStage("generating");
    setErrorMessage(null);
    setCategory(null);

    try {
      const response = isRedefine
        ? await redefineDoclistCategoryFromPages(
            organizationId,
            projectId,
            folderCategoryId,
            undefined,
            controller.signal
          )
        : await defineDoclistCategoryFromPages(
            organizationId,
            projectId,
            folderCategoryId,
            undefined,
            controller.signal
          );

      if (controller.signal.aborted) return;

      const nextForm = toFormData(response.documentCategory);
      setCategory(response.documentCategory);
      setFormData(nextForm);
      if (isRedefine) {
        const cached = queryClient.getQueryData<Category[]>(
          getListOrganizationCategoriesQueryKey(organizationId)
        );
        const prior = cached?.find(
          (c) => c.id === response.documentCategory.id
        );
        setPreviousData(prior ? toFormData(prior) : null);
      } else {
        setPreviousData(null);
      }
      setStage("review");

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getListOrganizationCategoriesQueryKey(organizationId),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, projectId),
        }),
      ]);
    } catch (error) {
      if (controller.signal.aborted) return;

      const message =
        error instanceof AxiosError
          ? (error.response?.data?.message ?? error.message)
          : error instanceof Error
            ? error.message
            : intl.formatMessage({
                id: "docList.defineCategory.error.fallback",
                defaultMessage: "Something went wrong. Please try again.",
              });

      setErrorMessage(message);
      setStage("error");
      logger.error("[DefineCategoryModal] generation failed", { error, mode });
    }
  }, [
    organizationId,
    projectId,
    folderCategoryId,
    queryClient,
    intl,
    isRedefine,
    mode,
  ]);

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
      setStage("generating");
      setCategory(null);
      setFormData(emptyForm());
      setPreviousData(null);
      setErrorMessage(null);
      return;
    }

    void runGeneration();

    return () => {
      abortRef.current?.abort();
    };
  }, [open, runGeneration]);

  const handleCancelGenerating = () => {
    abortRef.current?.abort();
    onOpenChange(false);
  };

  const handleClose = () => {
    abortRef.current?.abort();
    onOpenChange(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!category) return;

    try {
      await updateCategory({
        organizationId,
        categoryId: category.id.toString(),
        data: {
          name: formData.name,
          definition: formData.definition,
          color: category.color,
          isFallback: category.isFallback,
          namingConvention: formData.namingConvention,
          namingExample: formData.namingExample,
          expectedContent: formData.expectedContent,
          keyTopics: formData.keyTopics,
          recognitionTips: formData.recognitionTips,
          length: formData.length,
        },
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getListOrganizationCategoriesQueryKey(organizationId),
        }),
        queryClient.invalidateQueries({
          queryKey: getListOrganizationCategoryFoldersQueryKey(organizationId),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, projectId),
        }),
      ]);

      toast({
        title: intl.formatMessage(
          isRedefine
            ? {
                id: "docList.defineCategory.updatedToast",
                defaultMessage: "Category updated",
              }
            : {
                id: "docList.defineCategory.savedToast",
                defaultMessage: "Category saved",
              }
        ),
      });

      onOpenChange(false);
    } catch (error) {
      const description =
        error instanceof AxiosError
          ? (error.response?.data?.message ?? error.message)
          : error instanceof Error
            ? error.message
            : intl.formatMessage({
                id: "docList.defineCategory.error.fallback",
                defaultMessage: "Something went wrong. Please try again.",
              });

      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "docList.defineCategory.saveError",
          defaultMessage: "Failed to save changes",
        }),
        description,
      });
      logger.error("[DefineCategoryModal] save failed", { error });
    }
  };

  const isGenerating = stage === "generating";
  const isReview = stage === "review";
  const isError = stage === "error";

  const title = isReview ? (
    isRedefine ? (
      <FormattedMessage
        id="docList.defineCategory.redefine.reviewTitle"
        defaultMessage="Confirm Updated Category Definition"
      />
    ) : (
      <FormattedMessage
        id="docList.defineCategory.review.title"
        defaultMessage="Review Category Definition"
      />
    )
  ) : isError ? (
    isRedefine ? (
      <FormattedMessage
        id="docList.defineCategory.redefine.errorTitle"
        defaultMessage="Couldn’t Refine Category"
      />
    ) : (
      <FormattedMessage
        id="docList.defineCategory.error.title"
        defaultMessage="Couldn't Define Category"
      />
    )
  ) : isRedefine ? (
    <FormattedMessage
      id="docList.defineCategory.redefine.generatingTitle"
      defaultMessage="Refining “{name}” from {count, plural, one {# Document} other {# Documents}}"
      values={{ name: folderCategoryName, count: sectionCount }}
    />
  ) : (
    <FormattedMessage
      id="docList.defineCategory.generating.title"
      defaultMessage="Defining Category from {count, plural, one {# Document} other {# Documents}}"
      values={{ count: sectionCount }}
    />
  );

  const description = isReview ? (
    isRedefine ? (
      <FormattedMessage
        id="docList.defineCategory.redefine.reviewDescription"
        defaultMessage="Review the changes below before saving. Existing content was preserved where possible; highlighted text shows what changed."
      />
    ) : (
      <FormattedMessage
        id="docList.defineCategory.review.description"
        defaultMessage="AI-generated from the pages in “{name}”. Tweak anything below, then save — this is the definition future uploads will be classified against."
        values={{ name: folderCategoryName }}
      />
    )
  ) : isError ? (
    <FormattedMessage
      id="docList.defineCategory.error.description"
      defaultMessage="We couldn't generate a definition for “{name}”."
      values={{ name: folderCategoryName }}
    />
  ) : isRedefine ? (
    <FormattedMessage
      id="docList.defineCategory.redefine.generatingDescription"
      defaultMessage="Reading the latest pages grouped under “{name}” and refining the existing org-level definition. This usually takes a few seconds."
      values={{ name: folderCategoryName }}
    />
  ) : (
    <FormattedMessage
      id="docList.defineCategory.generating.description"
      defaultMessage="Analysing the pages grouped under “{name}” and drafting an org-level definition. This usually takes a few seconds."
      values={{ name: folderCategoryName }}
    />
  );

  const generatingContent = (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
      <div className="relative">
        {isRedefine ? (
          <RefreshCw className="w-10 h-10 text-primary/60" />
        ) : (
          <Sparkles className="w-10 h-10 text-primary/60" />
        )}
        <Loader2 className="absolute -bottom-1 -right-1 w-5 h-5 animate-spin text-primary" />
      </div>
      <p className="text-sm text-muted-foreground max-w-sm">
        {isRedefine ? (
          <FormattedMessage
            id="docList.defineCategory.redefine.generatingBody"
            defaultMessage="Comparing the current definition against the latest pages and refining where new evidence warrants it…"
          />
        ) : (
          <FormattedMessage
            id="docList.defineCategory.generating.body"
            defaultMessage="Reading section summaries, drafting naming conventions, and writing recognition tips…"
          />
        )}
      </p>
    </div>
  );

  const errorContent = (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
      <AlertCircle className="w-10 h-10 text-destructive" />
      <p className="text-sm text-foreground max-w-md">
        {errorMessage ?? (
          <FormattedMessage
            id="docList.defineCategory.error.fallback"
            defaultMessage="Something went wrong. Please try again."
          />
        )}
      </p>
    </div>
  );

  const renderDiffPreview = (field: FieldKey) => {
    if (!isRedefine || !previousData) return null;
    const before = previousData[field] ?? "";
    const after = formData[field] ?? "";
    if (before === after) return null;
    const parts = diffWordsWithSpace(before, after);
    return (
      <div className="mt-1 rounded-md border border-dashed border-border/60 bg-muted/40 px-2 py-1.5 text-xs leading-relaxed text-muted-foreground">
        <div className="text-[10px] uppercase tracking-wide opacity-70 mb-0.5">
          <FormattedMessage
            id="docList.defineCategory.redefine.changes"
            defaultMessage="Changes"
          />
        </div>
        <div className="whitespace-pre-wrap break-words">
          {parts.map((part, i) => {
            if (part.added) {
              return (
                <span
                  key={i}
                  className="bg-green-500/20 text-green-900 dark:text-green-200 rounded px-0.5"
                >
                  {part.value}
                </span>
              );
            }
            if (part.removed) {
              return (
                <span
                  key={i}
                  className="bg-red-500/20 text-red-900 dark:text-red-200 line-through rounded px-0.5"
                >
                  {part.value}
                </span>
              );
            }
            return <span key={i}>{part.value}</span>;
          })}
        </div>
      </div>
    );
  };

  const reviewContent = (
    <form
      onSubmit={handleSubmit}
      id="define-category-form"
      className="space-y-4 pb-4"
    >
      <div className="space-y-1">
        <Label htmlFor="define-name">
          <FormattedMessage id="categorySettings.name" />
        </Label>
        <Input
          id="define-name"
          value={formData.name}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, name: e.target.value }))
          }
          disabled={isSaving}
          className="shadow-none"
        />
        {renderDiffPreview("name")}
      </div>

      <div className="space-y-1">
        <Label htmlFor="define-definition">
          <FormattedMessage id="categorySettings.definition" />
        </Label>
        <AutoResizeTextarea
          id="define-definition"
          value={formData.definition}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setFormData((prev) => ({ ...prev, definition: e.target.value }))
          }
          disabled={isSaving}
          className="shadow-none"
        />
        {renderDiffPreview("definition")}
      </div>

      <div className="space-y-1">
        <Label htmlFor="define-naming-convention">
          <FormattedMessage id="categorySettings.namingConvention" />
        </Label>
        <Input
          id="define-naming-convention"
          value={formData.namingConvention}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              namingConvention: e.target.value,
            }))
          }
          disabled={isSaving}
          className="shadow-none"
        />
        {renderDiffPreview("namingConvention")}
      </div>

      <div className="space-y-1">
        <Label htmlFor="define-naming-example">
          <FormattedMessage id="categorySettings.examples" />
        </Label>
        <AutoResizeTextarea
          id="define-naming-example"
          value={formData.namingExample}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setFormData((prev) => ({
              ...prev,
              namingExample: e.target.value,
            }))
          }
          disabled={isSaving}
          className="shadow-none"
        />
        {renderDiffPreview("namingExample")}
      </div>

      <div className="space-y-1">
        <Label htmlFor="define-expected-content">
          <FormattedMessage id="categorySettings.expectedContent" />
        </Label>
        <AutoResizeTextarea
          id="define-expected-content"
          value={formData.expectedContent}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setFormData((prev) => ({
              ...prev,
              expectedContent: e.target.value,
            }))
          }
          disabled={isSaving}
          className="shadow-none"
        />
        {renderDiffPreview("expectedContent")}
      </div>

      <div className="space-y-1">
        <Label htmlFor="define-key-topics">
          <FormattedMessage id="categorySettings.keyTopics" />
        </Label>
        <AutoResizeTextarea
          id="define-key-topics"
          value={formData.keyTopics}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setFormData((prev) => ({ ...prev, keyTopics: e.target.value }))
          }
          disabled={isSaving}
          className="shadow-none"
        />
        {renderDiffPreview("keyTopics")}
      </div>

      <div className="space-y-1">
        <Label htmlFor="define-recognition-tips">
          <FormattedMessage id="categorySettings.recognitionTips" />
        </Label>
        <AutoResizeTextarea
          id="define-recognition-tips"
          value={formData.recognitionTips}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setFormData((prev) => ({
              ...prev,
              recognitionTips: e.target.value,
            }))
          }
          disabled={isSaving}
          className="shadow-none"
        />
        {renderDiffPreview("recognitionTips")}
      </div>

      <div className="space-y-1">
        <Label htmlFor="define-length">
          <FormattedMessage id="categorySettings.length" />
        </Label>
        <Input
          id="define-length"
          value={formData.length}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, length: e.target.value }))
          }
          disabled={isSaving}
          className="shadow-none"
        />
        {renderDiffPreview("length")}
      </div>
    </form>
  );

  const generatingFooter = (
    <Button type="button" variant="outline" onClick={handleCancelGenerating}>
      <FormattedMessage id="cancel" />
    </Button>
  );

  const errorFooter = (
    <>
      <Button type="button" variant="outline" onClick={handleClose}>
        <FormattedMessage id="cancel" />
      </Button>
      <Button type="button" onClick={() => void runGeneration()}>
        <FormattedMessage
          id="docList.defineCategory.error.retry"
          defaultMessage="Try Again"
        />
      </Button>
    </>
  );

  const reviewFooter = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={handleClose}
        disabled={isSaving}
      >
        <FormattedMessage id="cancel" />
      </Button>
      <Button type="submit" form="define-category-form" disabled={isSaving}>
        {isSaving ? (
          <FormattedMessage
            id="docList.defineCategory.review.saving"
            defaultMessage="Saving…"
          />
        ) : isRedefine ? (
          <FormattedMessage
            id="docList.defineCategory.redefine.update"
            defaultMessage="Update"
          />
        ) : (
          <FormattedMessage id="save" />
        )}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next && isGenerating) {
          handleCancelGenerating();
          return;
        }
        if (!next && isSaving) return;
        onOpenChange(next);
      }}
      title={title}
      description={description}
      content={
        isGenerating
          ? generatingContent
          : isError
            ? errorContent
            : reviewContent
      }
      footer={
        isGenerating ? generatingFooter : isError ? errorFooter : reviewFooter
      }
    />
  );
};

export default DefineCategoryModal;
